class CartAddons {
  constructor() {
    this.bindEvents();
    this.checkExistingAddons();
    this.observeCartDrawer();
  }

  bindEvents() {
    document.addEventListener('change', (event) => {
      if (event.target.classList.contains('cart-addons__checkbox')) {
        this.handleAddonToggle(event.target);
      }
    });
  }

  checkExistingAddons() {
    fetch('/cart.js')
      .then(response => response.json())
      .then(cart => {
        const checkboxes = document.querySelectorAll('.cart-addons__checkbox');
        checkboxes.forEach(checkbox => {
          const variantId = checkbox.dataset.variantId;
          const existsInCart = cart.items.some(item => item.variant_id == variantId);
          checkbox.checked = existsInCart;
        });
      })
      .catch(error => {
        console.error('Error checking existing addons:', error);
      });
  }

  async handleAddonToggle(checkbox) {
    const variantId = checkbox.dataset.variantId;
    const productName = checkbox.dataset.productName;
    
    checkbox.disabled = true;
    
    try {
      if (checkbox.checked) {
        await this.addAddonToCart(variantId, productName);
      } else {
        await this.removeAddonFromCart(variantId);
      }
      
      await this.updateCartDisplay();
    } catch (error) {
      console.error('Error toggling addon:', error);
      checkbox.checked = !checkbox.checked;
      this.showError('Failed to update cart. Please try again.');
    } finally {
      checkbox.disabled = false;
    }
  }

  async addAddonToCart(variantId, productName) {
    const formData = {
      'id': variantId,
      'quantity': 1,
      'properties': {
        '_addon_product': 'true',
        '_addon_name': productName
      }
    };

    const response = await fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify(formData)
    });

    if (!response.ok) {
      const errorText = await response.text();
      try {
        const errorData = JSON.parse(errorText);
        throw new Error(errorData.message || errorData.description || 'Failed to add product to cart');
      } catch (parseError) {
        throw new Error(`Failed to add product to cart (${response.status})`);
      }
    }

    return response.json();
  }

  async removeAddonFromCart(variantId) {
    const cartResponse = await fetch('/cart.js');
    if (!cartResponse.ok) {
      throw new Error('Failed to fetch cart');
    }
    
    const cart = await cartResponse.json();
    const addonItem = cart.items.find(item => item.variant_id == variantId);

    if (!addonItem) {
      return;
    }

    const updates = {};
    updates[addonItem.key] = 0;

    const response = await fetch('/cart/update.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify({ updates })
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to remove product from cart (${response.status})`);
    }

    return response.json();
  }

  async updateCartDisplay() {
    try {
      // Update cart icon
      const cartIconResponse = await fetch('/?section_id=cart-icon-bubble');
      const cartIconHTML = await cartIconResponse.text();
      const parser = new DOMParser();
      const cartIconDoc = parser.parseFromString(cartIconHTML, 'text/html');
      const newCartIcon = cartIconDoc.querySelector('#cart-icon-bubble');
      
      if (newCartIcon) {
        const existingCartIcon = document.querySelector('#cart-icon-bubble');
        if (existingCartIcon) {
          existingCartIcon.innerHTML = newCartIcon.innerHTML;
        }
      }
      
      // Update cart drawer if it exists and is open
      const cartDrawer = document.querySelector('cart-drawer');
      if (cartDrawer && cartDrawer.classList.contains('active')) {
        const drawerResponse = await fetch('/?section_id=cart-drawer');
        const drawerHTML = await drawerResponse.text();
        const drawerDoc = parser.parseFromString(drawerHTML, 'text/html');
        const newDrawerContent = drawerDoc.querySelector('#CartDrawer');
        
        if (newDrawerContent) {
          const existingDrawer = document.querySelector('#CartDrawer');
          if (existingDrawer) {
            existingDrawer.innerHTML = newDrawerContent.innerHTML;
            setTimeout(() => this.setupCartHandlers(), 100);
          }
        }
      }
      
      // Update cart page if we're on it
      if (window.location.pathname === '/cart') {
        const cartDataResponse = await fetch('/cart.js');
        const cartData = await cartDataResponse.json();
        
        // Update the cart items table
        const cartItemsTable = document.querySelector('.cart-items tbody');
        if (cartItemsTable && cartData.items.length > 0) {
          const cartSectionResponse = await fetch('/?section_id=main-cart-items');
          const cartSectionHTML = await cartSectionResponse.text();
          const cartDoc = parser.parseFromString(cartSectionHTML, 'text/html');
          
          const newCartItemsTable = cartDoc.querySelector('.cart-items tbody');
          if (newCartItemsTable) {
            cartItemsTable.innerHTML = newCartItemsTable.innerHTML;
          }
          
          // Update totals but preserve addons section
          const existingAddons = document.querySelector('.cart-addons');
          const addonsHTML = existingAddons ? existingAddons.outerHTML : null;
          
          const newTotalsSection = cartDoc.querySelector('.js-contents-totals');
          const currentTotalsSection = document.querySelector('.js-contents-totals');
          
          if (newTotalsSection && currentTotalsSection) {
            currentTotalsSection.innerHTML = newTotalsSection.innerHTML;
            
            if (addonsHTML) {
              currentTotalsSection.insertAdjacentHTML('afterbegin', addonsHTML);
            }
          }
          
          setTimeout(() => this.setupCartHandlers(), 300);
        }
      }
      
    } catch (error) {
      console.error('Error updating cart display:', error);
    }
  }

  setupCartHandlers() {
    const checkboxes = document.querySelectorAll('.cart-addons__checkbox');
    checkboxes.forEach(checkbox => {
      if (!checkbox.hasAttribute('data-listener-added')) {
        checkbox.setAttribute('data-listener-added', 'true');
      }
    });
    this.checkExistingAddons();
  }

  observeCartDrawer() {
    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer) {
      const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
            const classList = cartDrawer.classList;
            if (classList.contains('active') || classList.contains('animate')) {
              setTimeout(() => {
                this.checkExistingAddons();
              }, 500);
            }
          }
        });
      });
      
      observer.observe(cartDrawer, {
        attributes: true,
        attributeFilter: ['class']
      });
    }
    
    const cartIcon = document.querySelector('#cart-icon-bubble');
    if (cartIcon) {
      cartIcon.addEventListener('click', () => {
        setTimeout(() => {
          this.checkExistingAddons();
        }, 1000);
      });
    }
  }

  showError(message) {
    // Create a simple error notification
    const errorDiv = document.createElement('div');
    errorDiv.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      background: #f44336;
      color: white;
      padding: 12px 20px;
      border-radius: 4px;
      z-index: 10000;
      font-size: 14px;
    `;
    errorDiv.textContent = message;
    
    document.body.appendChild(errorDiv);
    
    // Remove after 3 seconds
    setTimeout(() => {
      errorDiv.remove();
    }, 3000);
  }
}

// Initialize cart addons
let cartAddonsInstance = null;

function initializeCartAddons() {
  if (!cartAddonsInstance && document.querySelectorAll('.cart-addons__checkbox').length > 0) {
    cartAddonsInstance = new CartAddons();
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initializeCartAddons);
} else {
  initializeCartAddons();
}

// Also initialize after a short delay to handle dynamic content
setTimeout(initializeCartAddons, 1000);